import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, User } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { APPLY } from "@/constants/testIds";

const STATES = [
  "Abia","Adamawa","Akwa Ibom","Anambra","Bauchi","Bayelsa","Benue","Borno","Cross River",
  "Delta","Ebonyi","Edo","Ekiti","Enugu","FCT - Abuja","Gombe","Imo","Jigawa","Kaduna","Kano",
  "Katsina","Kebbi","Kogi","Kwara","Lagos","Nasarawa","Niger","Ogun","Ondo","Osun","Oyo",
  "Plateau","Rivers","Sokoto","Taraba","Yobe","Zamfara",
];

export default function Apply() {
  const { user } = useAuth();
  const [submitted, setSubmitted] = useState(null);
  const [form, setForm] = useState({
    full_name: "", gender: "Male", date_of_birth: "", age: "", state_of_origin: "Lagos",
    state_of_residence: "Lagos", lga: "", phone: "", whatsapp: "", email: "", address: "",
    education: "Bachelor's Degree", occupation: "", organization: "", has_agribusiness: false,
    agribusiness_type: "Crop Farming", agribusiness_name: "", years_in_agric: "Less than 1 year",
    idea_description: "", problem_solved: "", food_security_impact: "", prize_use: "",
    why_you: "", what_makes_you_different: "", tv_experience: "", can_stay_60_days: true,
    can_participate_all: true, facebook: "", instagram: "", twitter: "", tiktok: "", linkedin: "",
    video_url: "",
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (user) {
      setForm((p) => ({
        ...p,
        full_name: p.full_name || user.name || "",
        email: p.email || user.email || "",
      }));
    }
  }, [user]);

  const u = (k) => (e) => {
    const val = e?.target?.type === "checkbox" ? e.target.checked : (e?.target?.value ?? e);
    setForm((p) => ({ ...p, [k]: val }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const payload = { ...form, age: form.age ? Number(form.age) : null };
      const res = await api.post("/applications", payload);
      toast.success("Application submitted! Governor Bago will respond within 24 hours.");
      setSubmitted({ id: res.data.id, name: form.full_name, state: form.state_of_origin });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-12 py-24">
        <div className="p-10 border border-[#2BA84A]/40 bg-[#2BA84A]/5 text-center">
          <CheckCircle2 size={56} className="text-[#2BA84A] mx-auto mb-6" />
          <div className="text-xs uppercase tracking-[0.3em] text-[#2BA84A] mb-3">
            Application Received
          </div>
          <h1
            className="text-4xl md:text-5xl font-black tracking-tighter leading-[1] mb-5"
            style={{ fontFamily: "'Cabinet Grotesk', 'Bricolage Grotesque', sans-serif" }}
          >
            Thank you, <span className="text-[#F26A1F]">{submitted.name.split(" ")[0]}</span>.
          </h1>
          <p className="text-white/70 leading-relaxed mb-2">
            Your application to represent <span className="text-[#2BA84A] font-bold">{submitted.state}</span> at
            the Agrovest Reality TV Show has been received.
          </p>
          <p className="text-white/60 text-sm leading-relaxed mb-8">
            Reference ID: <code className="text-[#F26A1F]">{submitted.id}</code><br />
            Governor Bago will acknowledge your application within 24 hours, and our team will contact
            you about your state-level audition date.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/how-it-works"
              className="h-12 px-6 inline-flex items-center gap-2 rounded-sm border border-white/10 hover:border-[#F26A1F] text-xs uppercase tracking-[0.2em] font-bold"
            >
              See next steps
            </Link>
            <Link
              to="/"
              className="h-12 px-6 inline-flex items-center gap-2 rounded-sm bg-[#F26A1F] hover:bg-[#D95A15] text-white text-xs uppercase tracking-[0.2em] font-bold"
            >
              Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-12 py-24">
      <div className="text-xs uppercase tracking-[0.3em] text-[#F26A1F] mb-5">
        Contestant Application
      </div>
      <h1
        className="text-5xl md:text-6xl font-black tracking-tighter leading-[0.95] mb-6"
        style={{ fontFamily: "'Cabinet Grotesk', 'Bricolage Grotesque', sans-serif" }}
      >
        Apply to <span className="text-[#F26A1F]">Season 1</span>.
      </h1>
      <p className="text-white/70 mb-8 max-w-2xl">
        Complete the full application. Governor Bago processes applications in real time and
        sends acknowledgement within 24 hours.
      </p>

      {user ? (
        <div className="mb-12 p-4 border border-[#2BA84A]/40 bg-[#2BA84A]/5 text-sm flex items-center gap-3">
          <User size={16} className="text-[#2BA84A]" />
          <span className="text-white/80">
            Signed in as <span className="text-[#2BA84A] font-bold">{user.name}</span> ({user.email}). Your application will be linked to this account.
          </span>
        </div>
      ) : (
        <div className="mb-12 p-4 border border-[#F26A1F]/40 bg-[#F26A1F]/5 text-sm flex flex-wrap items-center justify-between gap-3">
          <span className="text-white/80">
            Tip: <Link to="/register" className="text-[#F26A1F] font-bold underline">create an account</Link> first to track your application status.
          </span>
        </div>
      )}

      <form onSubmit={submit} data-testid={APPLY.form} className="space-y-12">
        <Section title="A · Personal Information">
          <Grid>
            <Input label="Full Name" value={form.full_name} onChange={u("full_name")} required />
            <Select label="Gender" value={form.gender} onChange={u("gender")} options={["Male", "Female"]} />
            <Input label="Date of Birth" type="date" value={form.date_of_birth} onChange={u("date_of_birth")} />
            <Input label="Age" type="number" value={form.age} onChange={u("age")} />
            <Select label="State of Origin" value={form.state_of_origin} onChange={u("state_of_origin")} options={STATES} />
            <Select label="State of Residence" value={form.state_of_residence} onChange={u("state_of_residence")} options={STATES} />
            <Input label="LGA" value={form.lga} onChange={u("lga")} />
            <Input label="Phone" value={form.phone} onChange={u("phone")} />
            <Input label="WhatsApp" value={form.whatsapp} onChange={u("whatsapp")} />
            <Input label="Email" type="email" value={form.email} onChange={u("email")} required />
          </Grid>
          <Input label="Residential Address" value={form.address} onChange={u("address")} />
        </Section>

        <Section title="B · Education & Employment">
          <Grid>
            <Select label="Highest Education" value={form.education} onChange={u("education")} options={["Secondary School", "OND/NCE", "HND", "Bachelor's Degree", "Master's Degree", "PhD", "Other"]} />
            <Input label="Occupation" value={form.occupation} onChange={u("occupation")} />
          </Grid>
          <Input label="Name of Organization / Business" value={form.organization} onChange={u("organization")} />
        </Section>

        <Section title="C · Agribusiness Information">
          <Grid>
            <Select label="Do you currently operate an agribusiness?" value={form.has_agribusiness ? "Yes" : "No"} onChange={(e) => setForm((p) => ({ ...p, has_agribusiness: e.target.value === "Yes" }))} options={["Yes", "No"]} />
            <Select label="Agribusiness Type" value={form.agribusiness_type} onChange={u("agribusiness_type")} options={["Crop Farming", "Livestock Farming", "Poultry", "Fish Farming", "Agritech", "Food Processing", "Agricultural Marketing", "Other"]} />
            <Input label="Agribusiness Name" value={form.agribusiness_name} onChange={u("agribusiness_name")} />
            <Select label="Years in Agriculture" value={form.years_in_agric} onChange={u("years_in_agric")} options={["Less than 1 year", "1–3 years", "4–6 years", "More than 6 years"]} />
          </Grid>
        </Section>

        <Section title="D · Your Agro Idea">
          <Textarea label="Briefly describe your agricultural business idea (max 300 words)" value={form.idea_description} onChange={u("idea_description")} />
          <Textarea label="What problem does your idea solve?" value={form.problem_solved} onChange={u("problem_solved")} />
          <Textarea label="How can your idea help reduce food insecurity in Nigeria?" value={form.food_security_impact} onChange={u("food_security_impact")} />
          <Textarea label="How will the ₦20 Million prize help scale your idea?" value={form.prize_use} onChange={u("prize_use")} />
        </Section>

        <Section title="E · Agrovest Competition">
          <Textarea label="Why should Nigeria choose you to represent your state?" value={form.why_you} onChange={u("why_you")} />
          <Textarea label="What makes you different from other contestants?" value={form.what_makes_you_different} onChange={u("what_makes_you_different")} />
          <Input label="Prior TV/Reality Experience (if any)" value={form.tv_experience} onChange={u("tv_experience")} />
        </Section>

        <Section title="F · Social Media & Public Profile">
          <Grid>
            <Input label="Facebook" value={form.facebook} onChange={u("facebook")} />
            <Input label="Instagram" value={form.instagram} onChange={u("instagram")} />
            <Input label="X (Twitter)" value={form.twitter} onChange={u("twitter")} />
            <Input label="TikTok" value={form.tiktok} onChange={u("tiktok")} />
            <Input label="LinkedIn" value={form.linkedin} onChange={u("linkedin")} />
          </Grid>
        </Section>

        <Section title="G · Video Submission">
          <Input label="Video URL (YouTube / Drive / WeTransfer)" value={form.video_url} onChange={u("video_url")} />
          <p className="text-xs text-white/50">
            1–3 minute video: Who are you? Which state are you representing? What is your agro
            business idea? Why should you be selected?
          </p>
        </Section>

        <button
          type="submit"
          disabled={sending}
          data-testid={APPLY.submit}
          className="w-full h-14 inline-flex items-center justify-center gap-2 rounded-sm bg-[#F26A1F] hover:bg-[#D95A15] disabled:opacity-60 text-white text-sm uppercase tracking-[0.2em] font-bold transition-colors"
        >
          {sending ? "Submitting..." : "Submit Application"}
        </button>
      </form>
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="space-y-5">
    <h2 className="text-xs uppercase tracking-[0.3em] text-[#F26A1F] font-bold border-b border-white/10 pb-2">
      {title}
    </h2>
    {children}
  </section>
);
const Grid = ({ children }) => <div className="grid md:grid-cols-2 gap-4">{children}</div>;
const Input = ({ label, value, onChange, type = "text", required }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      className="w-full h-11 px-3 bg-white/[0.03] border border-white/10 focus:border-[#F26A1F] outline-none text-sm rounded-sm"
    />
  </div>
);
const Textarea = ({ label, value, onChange }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">{label}</label>
    <textarea
      rows={3}
      value={value}
      onChange={onChange}
      className="w-full p-3 bg-white/[0.03] border border-white/10 focus:border-[#F26A1F] outline-none text-sm rounded-sm"
    />
  </div>
);
const Select = ({ label, value, onChange, options }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">{label}</label>
    <select
      value={value}
      onChange={onChange}
      className="w-full h-11 px-3 bg-white/[0.03] border border-white/10 focus:border-[#F26A1F] outline-none text-sm rounded-sm"
    >
      {options.map((o) => <option key={o} value={o} className="bg-black">{o}</option>)}
    </select>
  </div>
);
